import type { CartItem, CartItemAddOn } from '~/composables/types/cart'
import type { EventAddOn } from '~/composables/types/event'
import { getEventAddOnPriceType } from '~/utils/eventAddOnGetters'

export const normalizeCartItemAddOns = (item: CartItem): CartItemAddOn[] => {
  if (!item.addOn) return []
  return Array.isArray(item.addOn) ? item.addOn : [item.addOn]
}

export const filterAddOnsByBookingType = (
  addOns: EventAddOn[],
  isPrivate: boolean
): EventAddOn[] =>
  addOns.filter((addOn) =>
    isPrivate
      ? addOn.date_type.includes('PRIVATE')
      : addOn.date_type.includes('PUBLIC')
  )

export const isAddOnEligibleForTicketQuantity = (
  ticketQuantity: number,
  addOn: EventAddOn
): boolean => addOn.min_tickets == null || ticketQuantity >= addOn.min_tickets

export const filterAddOnsByMinTickets = (
  addOns: EventAddOn[],
  ticketQuantity: number
): EventAddOn[] =>
  addOns.filter((addOn) =>
    isAddOnEligibleForTicketQuantity(ticketQuantity, addOn)
  )

export const getMaxCartAddOnQuantity = (
  item: CartItem,
  addOn: EventAddOn
): number => {
  if (!isAddOnEligibleForTicketQuantity(item.quantity, addOn)) return 0

  const ticketCap =
    addOn.max_tickets != null
      ? Math.min(item.quantity, addOn.max_tickets)
      : item.quantity
  return Math.max(0, ticketCap)
}

/** Default quantity when a per-ticket addon is first selected (matches ticket count). */
export const getDefaultPerTicketAddOnQuantity = (
  item: Pick<CartItem, 'quantity'>,
  addOn: EventAddOn
): number =>
  Math.min(item.quantity, getMaxCartAddOnQuantity(item as CartItem, addOn))

const toAddOnList = (
  addOns: EventAddOn[] | EventAddOn | undefined
): EventAddOn[] => {
  if (!addOns) return []
  return Array.isArray(addOns) ? addOns : [addOns]
}

export const getCartAddOnQuantity = (
  item: CartItem,
  addOn: EventAddOn | CartItemAddOn
): number => {
  if (getEventAddOnPriceType(addOn) === 'PER_SESSION') {
    return normalizeCartItemAddOns(item).some((a) => a.id === addOn.id) ? 1 : 0
  }

  const cartAddOn = normalizeCartItemAddOns(item).find((a) => a.id === addOn.id)
  if (!cartAddOn) return 0

  return cartAddOn.selectedQuantity ?? item.quantity
}

export const isCartAddOnSelected = (
  item: CartItem,
  addOn: EventAddOn
): boolean => getCartAddOnQuantity(item, addOn) > 0

export const migrateCartItemAddOnQuantities = (item: CartItem): void => {
  const addOns = normalizeCartItemAddOns(item)
  if (!addOns.length) return

  const migrated = addOns
    .map((addOn) => {
      if (getEventAddOnPriceType(addOn) === 'PER_SESSION') {
        return isAddOnEligibleForTicketQuantity(item.quantity, addOn)
          ? addOn
          : null
      }

      const quantity = Math.min(
        addOn.selectedQuantity ?? item.quantity,
        getMaxCartAddOnQuantity(item, addOn)
      )

      if (quantity <= 0) return null

      return { ...addOn, selectedQuantity: quantity }
    })
    .filter((addOn): addOn is CartItemAddOn => addOn !== null)

  item.addOn = migrated.length ? migrated : undefined
}

export const clampCartItemAddOns = (item: CartItem): void => {
  migrateCartItemAddOnQuantities(item)
}

export const mergeCartItemAddOns = (
  existing: CartItemAddOn[],
  incoming: CartItemAddOn[]
): CartItemAddOn[] => {
  const merged = [...existing]

  incoming.forEach((addOn) => {
    const index = merged.findIndex((a) => a.id === addOn.id)
    if (index === -1) {
      merged.push(addOn)
      return
    }
    merged[index] = { ...merged[index], ...addOn }
  })

  return merged
}

export const enrichAddOnsForCart = (
  addOns: EventAddOn[] | EventAddOn | undefined,
  item: Pick<CartItem, 'quantity'>
): CartItemAddOn[] | undefined => {
  const list = toAddOnList(addOns)
  if (!list.length) return undefined

  return list.map((addOn) => {
    if (getEventAddOnPriceType(addOn) === 'PER_SESSION') {
      return { ...addOn }
    }
    return {
      ...addOn,
      selectedQuantity: getDefaultPerTicketAddOnQuantity(item, addOn),
    }
  })
}
